import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { SupplierItem } from '@/services/reparoly/suppliersApi';

export type SuppliersState = {
  items: SupplierItem[];
  selectedId: number;
  loaded: boolean;
};

const initialState: SuppliersState = {
  items: [],
  selectedId: 0,
  loaded: false,
};

export function normalizeSupplier(it: any): SupplierItem {
  return {
    id: Number(it?.id ?? it?.ID ?? 0),
    name: String(it?.name ?? it?.Name ?? ''),
    contact_info: it?.contact_info ?? it?.ContactInfo ?? it?.contact ?? null,
    is_placeholder: typeof it?.is_placeholder === 'boolean' ? it.is_placeholder : Boolean(it?.IsPlaceholder),
  };
}

function upsert(list: SupplierItem[], rec: SupplierItem): SupplierItem[] {
  const idx = list.findIndex((x) => x.id === rec.id);
  if (idx >= 0) {
    const next = list.slice();
    next[idx] = { ...next[idx], ...rec };
    return next;
  }
  return [rec, ...list];
}

const suppliersSlice = createSlice({
  name: 'suppliers',
  initialState,
  reducers: {
    setSuppliers(state, action: PayloadAction<{ items: any[] }>) {
      const incoming = (action.payload?.items ?? []).map(normalizeSupplier);
      // merge by id, keep existing when no data changes
      const map = new Map<number, SupplierItem>();
      for (const it of state.items) map.set(it.id, it);
      for (const it of incoming) map.set(it.id, { ...(map.get(it.id) || {} as any), ...it });
      state.items = Array.from(map.values());
      state.loaded = true;
    },
    upsertSupplier(state, action: PayloadAction<any>) {
      const rec = normalizeSupplier(action.payload);
      state.items = upsert(state.items, rec);
      state.loaded = true;
    },
    removeSupplier(state, action: PayloadAction<number>) {
      const id = action.payload;
      state.items = state.items.filter((x) => x.id !== id);
      if (state.selectedId === id) state.selectedId = 0;
    },
    clearSuppliers(state) {
      state.items = [];
      state.selectedId = 0;
      state.loaded = false;
    },
    setSelectedSupplierId(state, action: PayloadAction<number | null>) {
      state.selectedId = action.payload ?? 0;
    },
  },
});

export const { setSuppliers, upsertSupplier, removeSupplier, clearSuppliers, setSelectedSupplierId } = suppliersSlice.actions;
export default suppliersSlice.reducer;
